/**
 * The apps loan officers reach for most, grouped the way they think about them, shown above the
 * full Composio catalog (whose own order starts with developer tools like GitHub).
 *
 * `app` is the Composio toolkit slug (checked against Composio's catalog: OneDrive is `one_drive`,
 * SharePoint `share_point`). Gmail isn't here: it runs on Addy's own gmail-server, not Composio.
 * Logos come from Composio's logo service, the same URL its catalog returns; a broken one falls
 * back to the app's first letter.
 */
const COMPOSIO_LOGO = "https://logos.composio.dev/api/";

// `categories`: Composio's own categories for the section, whose apps See all lists after ours.
const group = (id, label, categories, apps) => ({
  id,
  label,
  categories,
  apps: apps.map(([app, name]) => ({ app, name, logo: `${COMPOSIO_LOGO}${app}` })),
});

// The page's sections, in this order; each opens as a full list ("See all"). E-signature apps sit
// with Files, where the signed documents end up.
export const FEATURED_APP_GROUPS = [
  group("productivity", "Productivity", ["productivity", "scheduling-&-booking", "notes"], [
    ["googlecalendar", "Google Calendar"],
    ["calendly", "Calendly"],
    ["microsoft_word", "Microsoft Word"],
    ["googledocs", "Google Docs"],
  ]),
  group("email", "Email", ["email"], [["outlook", "Outlook"]]),
  group("crm", "CRM", ["crm", "contact-management"], [
    ["salesforce", "Salesforce"],
    ["hubspot", "HubSpot"],
    ["pipedrive", "Pipedrive"],
    ["googlecontacts", "Google Contacts"],
  ]),
  group("files", "Files", ["file-management-&-storage", "signatures"], [
    ["one_drive", "OneDrive"],
    ["googledrive", "Google Drive"],
    ["share_point", "SharePoint"],
    ["dropbox", "Dropbox"],
    ["box", "Box"],
    ["docusign", "DocuSign"],
    ["dropbox_sign", "Dropbox Sign"],
    ["pandadoc", "PandaDoc"],
  ]),
  group("chat", "Chat", ["team-chat", "video-conferencing"], [
    ["microsoft_teams", "Microsoft Teams"],
    ["slack", "Slack"],
    ["zoom", "Zoom"],
    ["googlemeet", "Google Meet"],
  ]),
  group("sheets", "Spreadsheet", ["spreadsheets"], [
    ["excel", "Excel"],
    ["googlesheets", "Google Sheets"],
    ["airtable", "Airtable"],
  ]),
  group("tasks", "Tasks", ["task-management", "project-management"], [
    ["notion", "Notion"],
    ["asana", "Asana"],
    ["monday", "Monday"],
    ["trello", "Trello"],
    ["googletasks", "Google Tasks"],
  ]),
];

// The Featured row when there is no popularity data yet: a line on why to connect each.
export const SPOTLIGHT_APPS = [
  ["outlook", "Outlook", "Email and calendar"],
  ["one_drive", "OneDrive", "Files and documents"],
  ["docusign", "DocuSign", "Send and sign documents"],
].map(([app, name, description]) => ({ app, name, description, logo: `${COMPOSIO_LOGO}${app}` }));

const FEATURED_COUNT = 3;
const SECTION_OF = new Map(FEATURED_APP_GROUPS.flatMap(({ label, apps }) => apps.map(({ app }) => [app, label])));

/**
 * The apps the Featured row shows: the most connected first (`popular`, most first), then the default
 * picks to fill what is missing. `isOffered` leaves out apps already connected.
 */
export const pickFeatured = (popular, isOffered) => {
  const picks = [];
  for (const app of [...popular, ...SPOTLIGHT_APPS]) {
    if (picks.length === FEATURED_COUNT) break;
    if (isOffered(app) && !picks.some(({ app: picked }) => picked === app.app)) {
      picks.push({ ...app, logo: app.logo || `${COMPOSIO_LOGO}${app.app}` });
    }
  }

  return picks;
};

/** The line under a Featured card: why to connect it, or the section it is listed in. */
export const describeFeatured = ({ app, description }) => description || SECTION_OF.get(app) || "";
